import {
  FC,
  MutableRefObject,
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react';
import { usePopper } from 'react-popper';
import cn from 'classnames';
import * as PopperJS from '@popperjs/core';
import ReactDOM from 'react-dom';
import { usePortalContainer } from 'src/hooks/usePortalContainer';

interface Props {
  children: ReactElement | ReactElement[];
  parentRef: MutableRefObject<HTMLElement>;
  isOpen: boolean;
  className?: string;
  placement?: PopperJS.Placement;
  disabled?: boolean;
  withPortal?: boolean;
  customStyles?: { [key: string]: string | number };
}

const DropdownElement: FC<Props> = ({
  children,
  isOpen,
  withPortal = false,
  parentRef,
  className,
  placement = 'bottom',
  disabled = false,
  customStyles = {}
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const container = usePortalContainer();

  const [isElementReady, setIsElementReady] = useState(false);

  const popper = usePopper(parentRef?.current, popupRef?.current, {
    placement,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: [placement]
        }
      }
    ]
  });

  const {
    styles = {},
    attributes = {},
    forceUpdate = null
  } = disabled ? {} : popper;

  useEffect(() => {
    if (isOpen && popupRef?.current) {
      setIsElementReady(true);
    }

    if (!isOpen) {
      setIsElementReady(false);
    }
  }, [popupRef, isOpen, setIsElementReady]);

  useEffect(() => {
    if (forceUpdate && isOpen && isElementReady) {
      forceUpdate();
    }
  }, [isElementReady, forceUpdate, isOpen]);

  const dropdown = (
    <div
      style={isOpen ? { ...styles.popper, ...customStyles } : customStyles}
      {...attributes.popper}
      ref={popupRef}
      className={cn('flex bg-white w-full', className, !isOpen && 'hidden')}
    >
      {children}
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return withPortal && container
    ? ReactDOM.createPortal(dropdown, container)
    : dropdown;
};

export default DropdownElement;
